<!-- The tab fills its container: tiles and toolbar at their natural height, the creators card takes the
     rest, and only the card's body scrolls (between the sticky header row and the footer). On phones
     the tiles take most of the screen, so the tab scrolls too and the card gets a fixed 80vh. -->
<div class="mba flex h-full min-h-0 flex-1 flex-col font-(family-name:--font-fallback) text-(--fg-1) max-[680px]:overflow-y-auto">
    @if (brief()) {
    <div class="@container flex min-h-0 flex-1 flex-col gap-[22px] pb-4 max-[680px]:flex-none">

        <!-- Stage tiles -->
        <mbt-stage-tiles [summary]="tileSummary()" [kind]="kind()" [creatorsNeeded]="brief()!.requiredCreators"
            [active]="stageFilter()" (selected)="selectStage($event)" />

        <!-- Toolbar, or the bulk bar while rows are selected -->
        @if (selectedRows().length > 0) {
        <div role="toolbar" aria-label="Selected creators"
            class="flex min-h-[42px] shrink-0 flex-wrap items-center gap-2 rounded-full bg-(--shout-deep-purple) py-2 pr-2 pl-4 text-[13px] font-semibold text-white animate-[mba-in_220ms_cubic-bezier(0.2,0,0,1)] motion-reduce:animate-none">
            <span>{{ selectedRows().length }} selected</span>
            <span class="flex-1"></span>
            <button type="button" [class]="ui.btnOnDark" (click)="remindSelected()"><mba-icon name="bell" />Remind</button>
            <button type="button" [class]="ui.btnOnDark" *appHasRole="[4]" (click)="promoteSelected()"><mba-icon name="megaphone" />Promote</button>
            <button type="button" [class]="ui.btnOnDark" (click)="screenSelected()"><mba-icon name="scan" />Screen</button>
            <button type="button" [class]="ui.btnOnDark" (click)="clearSelection()"><mba-icon name="x" />Clear</button>
        </div>
        } @else {
        <div class="flex min-h-[42px] shrink-0 flex-wrap items-center gap-3">
            <label class="flex h-[38px] min-w-[200px] flex-[0_1_300px] items-center gap-2 rounded-full border border-(--border-subtle) bg-(--mba-surface) px-3 text-(--fg-3) transition-[border-color,box-shadow] duration-[140ms] focus-within:border-(--app-accent) focus-within:ring-[3px] focus-within:ring-(--app-accent-ring)">
                <mba-icon name="search" size="size-4" />
                <span class="sr-only">Search applicants</span>
                <input #searchInput type="text" [formField]="searchQueryForm.query" placeholder="Search applicants"
                    (keydown)="onSearchKeydown($event)"
                    class="min-w-0 flex-1 border-0 bg-transparent p-0 text-[13px] text-(--fg-1) shadow-none outline-none focus:ring-0" />
                <kbd aria-hidden="true" class="rounded-[5px] border border-(--border-default) px-1.5 py-px text-[11px] font-bold text-(--fg-3)">/</kbd>
            </label>
            <span class="relative">
                <label class="sr-only" for="mba-rating">Rating</label>
                <select id="mba-rating" [class]="selectClass" [value]="ratingFilter()" (change)="onRatingChange($event)">
                    <option value="all">All creators</option>
                    <option value="reviewed">Reviewed before</option>
                    <option value="new">New creators</option>
                </select>
                <mba-icon name="chevron-down" size="pointer-events-none absolute top-1/2 right-3 size-3.5 -translate-y-1/2 text-(--fg-3)" />
            </span>
            <mbt-assignment-filters mode="trigger" />
            @if (hasScoredQuestions()) {
            <button type="button" [attr.aria-pressed]="showQualifiedOnly()" (click)="showQualifiedOnly.set(!showQualifiedOnly())"
                class="inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-2 text-[13px] font-semibold focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-(--app-accent-ring)"
                [class]="showQualifiedOnly() ? 'border-(--app-accent-ring) bg-(--app-accent-soft) text-(--app-accent-strong)' : 'border-(--border-subtle) bg-(--mba-surface) text-(--fg-2)'">
                <i class="size-[7px] rounded-full" [class]="showQualifiedOnly() ? 'bg-(--success)' : 'bg-(--border-strong)'"></i>Qualified only</button>
            }
            <span class="flex-1"></span>
            <span [class]="ui.sub">{{ matchingCount() }} of {{ totalOnBrief() }} creators</span>
            <span class="relative">
                <label class="sr-only" for="mba-sort">Sort</label>
                <select id="mba-sort" [class]="selectClass" [value]="selectedSortId()" (change)="onSortChange($event)">
                    @for (option of sortOptions(); track option.id) {
                    <option [value]="option.id">{{ option.name }}</option>
                    }
                </select>
                <mba-icon name="chevron-down" size="pointer-events-none absolute top-1/2 right-3 size-3.5 -translate-y-1/2 text-(--fg-3)" />
            </span>
            <atom-broadcast-message *appHasRole="[4]" [briefId]="brief()!.id" [triggerClass]="ui.btnOutline"></atom-broadcast-message>
            <a [class]="ui.btnPrimary" [routerLink]="getInviteCreatorsLink()"><mba-icon name="user-plus" />Invite creators</a>
        </div>
        }
        <mbt-assignment-filters mode="chips" />

        <!-- Creators: fills the remaining height; the body scrolls, the header row and footer stay put -->
        <div class="flex min-h-0 flex-1 flex-col overflow-hidden {{ ui.card }} max-[680px]:h-[80vh] max-[680px]:flex-none">
            <div #scrollContainer class="min-h-0 flex-1 overflow-auto" infinite-scroll [infiniteScrollDistance]="2"
                [infiniteScrollThrottle]="100" [scrollWindow]="false" [immediateCheck]="true"
                [infiniteScrollContainer]="scrollContainer"
                [infiniteScrollDisabled]="!manageBriefTabsService.hasMoreApplicants() || manageBriefTabsService.isLoadingMore()"
                (scrolled)="onScrollDown()">
                @if (rows() === undefined) {
                <table class="w-full min-w-[1040px] border-collapse text-[13px]" aria-busy="true">
                    <tbody>
                        @for (skeleton of [1, 2, 3, 4, 5]; track skeleton) {
                        <tr>
                            <td [class]="td + ' w-11'"><span [class]="ui.skeleton + ' w-[18px]'"></span></td>
                            <td [class]="td"><span [class]="ui.skeleton + ' w-[180px]'"></span></td>
                            <td [class]="td"><span [class]="ui.skeleton + ' w-[120px]'"></span></td>
                            <td [class]="td"><span [class]="ui.skeleton + ' w-[70px]'"></span></td>
                            <td [class]="td"><span [class]="ui.skeleton + ' w-[90px]'"></span></td>
                            <td [class]="td"><span [class]="ui.skeleton + ' w-[140px]'"></span></td>
                            <td [class]="td"><span [class]="ui.skeleton + ' ml-auto w-[120px]'"></span></td>
                        </tr>
                        }
                    </tbody>
                </table>
                } @else if (rows()!.length === 0) {
                <div class="flex h-full flex-col items-center justify-center gap-1.5 px-5 py-9 text-center">
                    @switch (emptyKind()) {
                    @case ('caught-up') {
                    <span [class]="emptyIcon"><mba-icon name="check-check" /></span>
                    <b [class]="emptyTitle">All caught up</b>
                    <span [class]="emptyBody">Nothing is waiting on you right now. We'll show new applications and videos here as they arrive.</span>
                    }
                    @case ('no-match') {
                    <span [class]="emptyIcon"><mba-icon name="search-x" /></span>
                    <b [class]="emptyTitle">No creators match</b>
                    <span [class]="emptyBody">Try a different search, stage or filter.</span>
                    <div class="mt-2.5 flex flex-wrap justify-center gap-2">
                        <button type="button" [class]="ui.btnOutline" (click)="resetFilters()">Show all creators</button>
                    </div>
                    }
                    @default {
                    <span [class]="emptyIcon"><mba-icon name="users" /></span>
                    <b [class]="emptyTitle">No creators yet</b>
                    <span [class]="emptyBody">Invite creators who match the brief, or turn on Auto Pilot and SHOUT invites and shortlists
                        matching creators for you. Everyone shows here as they apply, get hired and {{ kind() === 'influence' ? 'post' : 'deliver' }}.</span>
                    <div class="mt-2.5 flex flex-wrap justify-center gap-2">
                        <a [class]="ui.btnPrimary" [routerLink]="getInviteCreatorsLink()"><mba-icon name="user-plus" />Invite creators</a>
                        @if (!brief()!.enableAutoPilot) {
                        <button type="button" [class]="ui.btnOutline" (click)="turnOnAutoPilot()"><mba-icon name="rocket" />Turn on Auto Pilot</button>
                        }
                    </div>
                    }
                    }
                </div>
                } @else {
                <!-- Table (wide) -->
                <table class="w-full min-w-[1040px] border-collapse text-[13px] @max-[860px]:hidden">
                    <thead>
                        <tr>
                            <th scope="col" [class]="th + ' w-11 pr-0'">
                                <button type="button" role="checkbox" aria-label="Select all visible creators" (click)="toggleAll()"
                                    [attr.aria-checked]="allSelected() ? 'true' : someSelected() ? 'mixed' : 'false'"
                                    [class]="checkboxClass(allSelected() || someSelected())">
                                    @if (allSelected()) { <mba-icon name="check" size="size-3" [strokeWidth]="3" /> }
                                    @else if (someSelected()) { <mba-icon name="minus" size="size-3" [strokeWidth]="3" /> }
                                </button>
                            </th>
                            <th scope="col" [class]="th">Creator</th>
                            <th scope="col" [class]="th">Stage</th>
                            <th scope="col" [class]="th + ' @max-[1200px]:hidden'">Rating</th>
                            <th scope="col" [class]="th + ' @max-[1200px]:hidden'">Screening</th>
                            <th scope="col" [class]="th">Brief videos</th>
                            @if (showCrossPlatformColumns()) {
                            <th scope="col" [class]="th">Primary post</th>
                            <th scope="col" [class]="th">Cross-posts</th>
                            <th scope="col" [class]="th">Spend</th>
                            }
                            @if (showsShipping()) {
                            <th scope="col" [class]="th">Shipping</th>
                            }
                            <th scope="col" [class]="th + ' right-0 z-20 text-right shadow-[-12px_0_12px_-12px_rgba(18,6,43,0.18)]'">Next step</th>
                        </tr>
                    </thead>
                    <tbody class="[&>tr:last-child>td]:border-b-0">
                        @for (row of rows(); track row.applicant.id) {
                        <tr tabindex="0" [class]="rowClass(row)" [title]="rowTitle(row)"
                            (click)="onRowActivate(row, $event)" (keydown)="onRowActivate(row, $event)">
                            <td [class]="td + ' w-11 pr-0' + (row.needsYou && !row.expired ? ' shadow-[inset_3px_0_0_var(--app-accent)]' : '')">
                                <button type="button" role="checkbox" [attr.aria-checked]="selectedIds().has(row.applicant.id)"
                                    [attr.aria-label]="'Select ' + row.name" (click)="toggleRow(row.applicant.id)"
                                    [class]="checkboxClass(selectedIds().has(row.applicant.id))">
                                    @if (selectedIds().has(row.applicant.id)) { <mba-icon name="check" size="size-3" [strokeWidth]="3" /> }
                                </button>
                            </td>
                            <td [class]="td">
                                <ng-container [ngTemplateOutlet]="who" [ngTemplateOutletContext]="{ $implicit: row }" />
                            </td>
                            <td [class]="td">
                                <ng-container [ngTemplateOutlet]="stageCell" [ngTemplateOutletContext]="{ $implicit: row }" />
                            </td>
                            <td [class]="td + ' @max-[1200px]:hidden'">
                                <ng-container [ngTemplateOutlet]="ratingCell" [ngTemplateOutletContext]="{ $implicit: row }" />
                            </td>
                            <td [class]="td + ' @max-[1200px]:hidden'">
                                <ng-container [ngTemplateOutlet]="screeningCell" [ngTemplateOutletContext]="{ $implicit: row }" />
                            </td>
                            <td [class]="td">
                                @if (row.stage >= 2) {
                                <mbt-video-status-pill [counts]="row.counts" />
                                } @else {
                                <span [class]="ui.dash">&mdash;</span>
                                }
                            </td>
                            @if (showCrossPlatformColumns()) {
                            <td [class]="td" data-row-click-ignore>
                                <mbt-cross-platform-tile [socialChannel]="brief()!.shoutBrief!.socialPlatform"
                                    [state]="primaryTileState(row.applicant)" [label]="briefPrimaryChannelLabel()"
                                    [postStatus]="primaryPostStatus(row.applicant)" [interactive]="false"></mbt-cross-platform-tile>
                            </td>
                            <td [class]="td" data-row-click-ignore>
                                <div class="flex flex-col gap-[3px]">
                                    <div class="flex flex-wrap items-center gap-2">
                                        @for (cell of crossPlatformCells(row.applicant); track cell.socialChannel) {
                                        <mbt-cross-platform-tile [socialChannel]="cell.socialChannel" [state]="cell.state"
                                            [label]="cell.label" [postStatus]="cell.postStatus"
                                            [price]="crossPlatformPrice(row.applicant, cell.socialChannel)" [currencyCode]="briefCurrencyCode()"
                                            [interactive]="isChannelTogglable(row.applicant, cell.socialChannel)"
                                            (toggled)="onToggleCrossPlatform(row.applicant, $event)"></mbt-cross-platform-tile>
                                        }
                                    </div>
                                    @if (crossPlatformSummary(row.applicant); as summary) {
                                    <span [class]="ui.sub">{{ summary }}</span>
                                    }
                                </div>
                            </td>
                            <td [class]="td">
                                <div class="flex flex-col gap-[3px]">
                                    @let spend = spendHeadline(row.applicant);
                                    @if (spend !== null) {
                                    <b class="font-(family-name:--font-display) text-base font-extrabold text-(--fg-1)">{{ spend | currency: briefCurrencyCode() }}</b>
                                    } @else {
                                    <b class="font-(family-name:--font-display) text-base font-extrabold text-(--fg-3)" title="Pricing this hire">&hellip;</b>
                                    }
                                    @if (crossPlatformSpendDelta(row.applicant) > 0) {
                                    <span class="text-xs font-bold text-(--app-accent-strong)">+{{ crossPlatformSpendDelta(row.applicant) | currency: briefCurrencyCode() }} cross-posts</span>
                                    } @else {
                                    <span [class]="ui.sub">{{ isApplicantRow(row.applicant) ? 'hire cost' : 'committed' }}</span>
                                    }
                                </div>
                            </td>
                            }
                            @if (showsShipping()) {
                            <td [class]="td">
                                <ng-container [ngTemplateOutlet]="shippingCell" [ngTemplateOutletContext]="{ $implicit: row }" />
                            </td>
                            }
                            <td [class]="actionCellClass(row)">
                                <ng-container [ngTemplateOutlet]="actions" [ngTemplateOutletContext]="{ $implicit: row }" />
                            </td>
                        </tr>
                        }
                        @if (manageBriefTabsService.isLoadingMore()) {
                        <tr>
                            <td [attr.colspan]="visibleColumnCount()" [class]="ui.sub + ' p-3.5 text-center'">Loading more&hellip;</td>
                        </tr>
                        }
                    </tbody>
                </table>

                <!-- Cards (narrow) -->
                <div class="hidden divide-y divide-(--border-subtle) @max-[860px]:block">
                    @for (row of rows(); track row.applicant.id) {
                    <div tabindex="0" [title]="rowTitle(row)" (click)="onRowActivate(row, $event)" (keydown)="onRowActivate(row, $event)"
                        class="flex cursor-pointer flex-col gap-3 px-4 py-3.5 focus-visible:outline-none focus-visible:bg-(--mba-row-hi)"
                        [class]="cardClass(row)">
                        <div [class]="cardLine">
                            <ng-container [ngTemplateOutlet]="who" [ngTemplateOutletContext]="{ $implicit: row }" />
                            <ng-container [ngTemplateOutlet]="ratingCell" [ngTemplateOutletContext]="{ $implicit: row }" />
                        </div>
                        <div [class]="cardLine">
                            <ng-container [ngTemplateOutlet]="stageCell" [ngTemplateOutletContext]="{ $implicit: row }" />
                            @if (row.stage >= 2) {
                            <mbt-video-status-pill [counts]="row.counts" />
                            }
                        </div>
                        <div [class]="cardLine">
                            <span [class]="ui.sub">{{ row.expired ? 'Application expired' : row.step.why }}</span>
                            <ng-container [ngTemplateOutlet]="actions" [ngTemplateOutletContext]="{ $implicit: row }" />
                        </div>
                    </div>
                    }
                    @if (manageBriefTabsService.isLoadingMore()) {
                    <div [class]="ui.sub + ' p-3.5 text-center'">Loading more&hellip;</div>
                    }
                </div>
                }
            </div>

            @if (rows()?.length) {
            <div class="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-(--border-subtle) bg-(--bg-alt) px-4 py-3">
                <span class="inline-flex items-center gap-1.5 {{ ui.sub }}"><mba-icon name="info" size="size-[15px]" />Rows marked on the left need you. Click a hired
                    creator to open their submission, or a photo to see their SHOUT profile.</span>
            </div>
            }
        </div>
    </div>

    <!-- Side panel -->
    @if (openRow(); as row) {
    <mbt-creator-side-panel [applicant]="row.applicant" [brief]="brief()" [kind]="kind()" [stageLabels]="stageLabels()"
        [step]="row.step" [needsYou]="row.needsYou" [nowMs]="nowMs()" [autoDeclineDays]="brandAutoDeclineDays()"
        [autoDeclineOn]="autoDeclineOn(row.applicant)" [expired]="row.expired" [busy]="isBusy(row.applicant)"
        [position]="openIndex()" [total]="rows()!.length" [canUnhire]="canUnhire(row.applicant)"
        [channelLabel]="channelName()" [costText]="costText(row.applicant)" [costNote]="costNote(row)"
        [hireInPanel]="hireInPanel()" (hire)="hireFromPanel(row)"
        (closed)="openId.set(null)" (previous)="movePanel(-1)" (next)="movePanel(1)" (primary)="onPrimary(row)"
        (decline)="openDecline(row)" (withdraw)="withdrawInvite(row)" (unhire)="openUnhire(row)"
        (openProfile)="toggleCreatorPortfolio(row.applicant)" (copyInvite)="copyTtoInviteLink($event)" />
    }

    <!-- Decline / un-hire -->
    @if (modal(); as m) {
    <mbt-reason-modal [mode]="m.kind" [firstName]="m.applicant.creator?.firstName ?? ''" [fullName]="nameOf(m.applicant)"
        [hasUploads]="hasUploads(m.applicant)" (closed)="modal.set(null)" (confirmed)="onReasonConfirmed($event)" />
    }
    } @else {
    <p class="p-6 {{ ui.sub }}">No brief selected</p>
    }
</div>

<!-- ── Shared cell templates (table + cards) ─────────────────────────────── -->

<ng-template #who let-row>
    <div class="flex min-w-0 items-center gap-3">
        <span role="link" tabindex="0" data-row-click-ignore [class]="ui.avatar + ' ' + ui.avatarLink"
            [title]="'View ' + row.name + '’s SHOUT profile'" [attr.aria-label]="'View ' + row.name + '’s SHOUT profile'"
            (click)="toggleCreatorPortfolio(row.applicant)" (keydown.enter)="toggleCreatorPortfolio(row.applicant)">
            @if (row.photo && !brokenPhotos().has(row.applicant.id)) {
            <img class="size-full rounded-full object-cover" [src]="row.photo" alt=""
                (error)="markPhotoBroken(row.applicant.id)" />
            } @else {
            {{ row.initial }}
            }
            @if (row.applicant.creator?.isVerified) {
            <span [class]="ui.verifiedBadge" title="Verified creator"><mba-icon name="check" size="size-2.5" [strokeWidth]="3" /></span>
            }
            @if (row.applicant.creator?.ssCreator) {
            <span *appHasRole="[4]" class="absolute -top-1.5 -left-1.5 rounded bg-emerald-50 px-1 py-px text-[9px] font-bold text-emerald-700">SS</span>
            }
        </span>
        <span class="flex min-w-0 flex-col gap-1">
            <b class="font-(family-name:--font-display) text-lg leading-none font-bold text-(--fg-1)">{{ row.name }}</b>
            <span class="flex flex-wrap gap-2.5 text-xs text-(--fg-2)">
                @if (row.applicant.creator?.tikTokFollowers) {
                <span class="inline-flex items-center gap-1"><img [class]="ui.platformLogo" src="assets/social-icon/tik-tok-transparent.svg" alt="TikTok" />{{ row.applicant.creator.tikTokFollowers | shortNumber }}</span>
                }
                @if (row.applicant.creator?.instaFollowers) {
                <span class="inline-flex items-center gap-1"><img [class]="ui.platformLogo" src="assets/social-icon/instagram-logo.png" alt="Instagram" />{{ row.applicant.creator.instaFollowers | shortNumber }}</span>
                }
                @if (row.applicant.creator?.youtubeSubscribers) {
                <span class="inline-flex items-center gap-1"><img [class]="ui.platformLogo" src="assets/social-icon/youtube_shorts.webp" alt="YouTube" />{{ row.applicant.creator.youtubeSubscribers | shortNumber }}</span>
                }
                @if (kind() === 'influence' && row.avgViews) {
                <span class="text-(--fg-3)">{{ row.avgViews | shortNumber }} avg views &middot; {{ row.engagement | number: '1.1-2' }}%</span>
                }
            </span>
        </span>
    </div>
</ng-template>

<ng-template #stageCell let-row>
    @if (row.stage === 1 && autoDeclineOn(row.applicant)) {
    <mbt-application-countdown [autoDeclineAt]="row.applicant.autoDeclineAt"
        [appliedAt]="row.applicant.applicationStatusModifiedDate || row.applicant.createdDate"
        [autoDeclineDays]="brandAutoDeclineDays()" [nowMs]="nowMs()" />
    } @else {
    <mbt-stage-segments [stage]="row.stage" [label]="row.stageLabel" [elapsed]="row.elapsed" [late]="row.late" />
    }
</ng-template>

<ng-template #ratingCell let-row>
    @if (row.applicant.creator?.reviews_summary?.average_rating > 0) {
    <span class="inline-flex items-center gap-1.5 text-xs text-(--fg-3)"><mba-icon name="star" size="size-3.5 fill-(--star) text-(--star)" />
        <b class="font-(family-name:--font-display) text-lg font-extrabold text-(--fg-1)">{{ row.applicant.creator.reviews_summary.average_rating | number: '1.1-1' }}</b>
        {{ row.applicant.creator.reviews_summary.total_reviews }} {{ row.applicant.creator.reviews_summary.total_reviews === 1 ? 'review' : 'reviews' }}</span>
    } @else {
    <span [class]="ui.newCreatorPill"><mba-icon name="sparkles" size="size-[13px]" />New creator</span>
    }
</ng-template>

<ng-template #screeningCell let-row>
    @if (row.stage === 0 || !hasScreening()) {
    <span [class]="ui.dash">&mdash;</span>
    } @else if (row.stage === 1) {
    @if (row.verdict; as v) {
    <span class="mr-2 inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-bold" [class]="verdictClass(v.verdict)">{{ v.verdict === 'review' ? 'Review' : v.passed + ' of ' + v.total }}</span>
    }
    <button type="button" (click)="openDetails(row)"
        class="inline-flex cursor-pointer items-center gap-[5px] rounded-md font-bold text-(--app-accent-strong) hover:underline {{ ui.focusRing }}"><mba-icon name="eye" size="size-[15px]" />Review answers</button>
    } @else {
    <span class="inline-flex items-center gap-[5px] font-semibold text-(--success)"><mba-icon name="check-circle" size="size-[15px]" />Passed</span>
    }
</ng-template>

<ng-template #shippingCell let-row>
    @if (showsShippingAction(row.applicant)) {
    @if (row.applicant.productShipment && row.applicant.productShipment.status !== 'Not-Shipped') {
    <span class="inline-flex items-center gap-1.5 rounded-full px-[9px] py-[3px] text-[11px] font-bold"
        [class]="row.applicant.productShipment.status === 'Delivered' ? 'bg-(--success)/10 text-(--success)' : 'bg-blue-600/10 text-blue-600'">{{ row.applicant.productShipment.status }}</span>
    } @else {
    <button type="button" [class]="ui.btnGhost" title="Manage shipping in the Logistics tab"
        (click)="navigateToLogistics(row.applicant)"><mba-icon name="truck" />Ship</button>
    }
    } @else {
    <span [class]="ui.dash">&mdash;</span>
    }
</ng-template>

<ng-template #actions let-row>
    <mbt-next-step-cell [step]="row.step" [busy]="isBusy(row.applicant)" [disabled]="row.expired"
        [canWithdraw]="row.stage === 0" [price]="costText(row.applicant)" (primary)="onPrimary(row)" (decline)="openDecline(row)"
        (openDetails)="openDetails(row)" (withdraw)="withdrawInvite(row)" />
</ng-template>
